import { Check, Copy, Share2 } from 'lucide-react';
import { useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';

import { QrPanel, type QrBranding } from './qr-panel';

/**
 * Copy a link, download its QR code, or hand it to the device's share sheet.
 *
 * Used for giving links and project pages — things a church puts on a screen behind
 * the pulpit and in a WhatsApp group in the same minute.
 */

export interface ShareDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  url: string;
  title: string;
  description?: string;
  labels: {
    copy: string;
    copied: string;
    share: string;
    download: string;
    close: string;
    qrLabel: string;
  };
  /** Filename stem for the QR download. */
  downloadName?: string;
  /** Passed to the share sheet as the message title. */
  shareTitle?: string;
  /** Church name and campus drawn onto the downloaded QR image. */
  branding?: QrBranding;
}

export const ShareDialog = ({
  open,
  onOpenChange,
  url,
  title,
  description,
  labels,
  downloadName,
  shareTitle,
  branding,
}: ShareDialogProps) => {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    await navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const canShare = typeof navigator !== 'undefined' && 'share' in navigator;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="sm" closeLabel={labels.close}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description ? <DialogDescription>{description}</DialogDescription> : null}
        </DialogHeader>

        <QrPanel
          value={url}
          size={180}
          label={labels.qrLabel}
          downloadLabel={labels.download}
          downloadName={downloadName}
          branding={branding}
        />

        <div className="flex gap-2">
          <Input readOnly value={url} onFocus={(event) => event.target.select()} />
          <Button
            variant="outline"
            onClick={copy}
            leadingIcon={
              copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />
            }
          >
            {copied ? labels.copied : labels.copy}
          </Button>
        </div>

        <DialogFooter>
          {canShare ? (
            <Button
              variant="secondary"
              onClick={() => void navigator.share({ title: shareTitle ?? title, url })}
              leadingIcon={<Share2 className="h-4 w-4" />}
            >
              {labels.share}
            </Button>
          ) : null}
          <Button onClick={() => onOpenChange(false)}>{labels.close}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
